import {
  castCustomStepSlice,
  castIdentificationToObject,
  castListToObject,
  castNavigationToList,
  castObjectToList,
  castPrismicObject,
  castStepOrderSliceToList,
  clean,
  getAdvancedCss,
  getBusinessTheme,
  getColors,
  getImages,
  getTheme,
} from './utils';
import {
  Configuration,
  ConfigurationKeys,
  CustomStep,
  Environment,
  PolicyData,
  RawConfiguration,
  RegexData,
  TimeSlotData,
} from 'ts/types/config.types';
import { PrismicRaw, RawPolicyData, RawTimeSlotData } from './prismic.types';
import { Map, StringMap } from 'ts/types/common.types';

export const defaultPrismicReducer = (acc: any, current: any) => ({
  ...acc,
  ...current,
});

export const defaultFinalCast = (item: any): Configuration => item;

export const decisionTreeReducer = (
  acc: { [_: string]: string },
  { language, fileName }: { [_: string]: string },
): { [_: string]: string } => ({
  ...acc,
  [language]: fileName,
});

export const surveyReducer = (
  acc: { [_: string]: string },
  { virtualPageView, surveyId }: { [_: string]: string },
): { [_: string]: string } => ({
  ...acc,
  [virtualPageView]: surveyId,
});

export const emergencyBanner = (
  acc: { [_: string]: string },
  { language, message }: { [_: string]: string },
): { [_: string]: string } => ({
  ...acc,
  [language]: message,
});

export const experimentsReducer = (
  acc: any,
  {
    customExperimentNameId,
    payload,
    experimentId,
  }: { customExperimentNameId: string; payload: any; experimentId: string },
) => ({
  ...acc,
  [customExperimentNameId]: {
    experimentId,
    payload: castPrismicObject(payload),
  },
});
export const regexReducer = (
  acc: { [_: string]: RegexData },
  { name, value, flag }: { [_: string]: string },
): { [_: string]: RegexData } => ({
  ...acc,
  [name]: {
    regex: value,
    flag: flag,
  },
});

export const environmentObjectReducer = (
  acc: Map,
  { environment, ...data }: Map,
): Map => ({
  ...acc,
  [environment]: data,
});

export const environmentSingleKeyReducer = (
  acc: Map,
  { environment, ...rest }: { [_: string]: string },
): Map => ({
  ...acc,
  [environment]: Object.values(rest)[0],
});

export const policyReducer = (
  acc: { [_: string]: PolicyData },
  { policy, ...policyData }: RawPolicyData,
): { [_: string]: PolicyData } => ({
  ...acc,
  [policy]: policyData,
});

const listToObjectBy = (originalList: Map[] = [], key: string): Map =>
  originalList.reduce(
    (acc, current) => ({ ...acc, [current[key]]: current }),
    {},
  );

export const customStepReducer = (
  acc: CustomStep[],
  current: CustomStep[],
): CustomStep[] =>
  Object.values({
    ...listToObjectBy(acc, 'page_name_analytics'),
    ...listToObjectBy(current, 'page_name_analytics'),
  });

export const simpleListReducer = (acc: any[], current: any) =>
  current?.length ? current : acc || [];

export const listReducer = (
  acc: any,
  current: any,
  obligatoryPopulatedFields: string[] = [],
) =>
  current?.length &&
  Object.values(current[0]).some(value => value !== null) &&
  (!obligatoryPopulatedFields.length ||
    obligatoryPopulatedFields.every(key => current[0][key] !== null))
    ? current
    : acc || [];

const timeSlotOptionsMap = ({
  id,
  start_time,
  end_time,
  ...data
}: RawTimeSlotData): TimeSlotData => {
  const paddedStartTime = `0${start_time}`;
  const paddedEndTime = `0${end_time}`;
  return {
    id: parseInt(id, 10),
    start_time:
      start_time && paddedStartTime.substr(paddedStartTime.length - 5),
    end_time: end_time && paddedEndTime.substr(paddedEndTime.length - 5),
    ...data,
  };
};

const getDefaultColors = (theme: any) =>
  Object.keys(theme).reduce((acc, key) => {
    if (key.includes('-')) {
      const [proposedKey, fallbackValue] = key.split('-');
      const actualKey = theme.hasOwnProperty(fallbackValue) ? proposedKey : key;
      return { ...acc, [actualKey]: theme[key] || theme[fallbackValue] };
    } else {
      return { ...acc, [key]: theme[key] };
    }
  }, {});

export const initialConfiguration = {
  decision_tree: {},
  emergency_banner_messages: {},
  google_optimize_experiments_list: {},
  gtm_env: {},
  identification: {},
  map_default_location: {},
  next_steps_per_page: {},
  navigation_default: [],
  navigation_desktop: [],
  one_trust_env: {},
  po_tools_env: {},
  policies: {},
  regex: {},
  service_qualifier_tree: {},
  survey_per_page: {},
  theme: {},
};

export const configPrismicReducer = (
  acc: RawConfiguration,
  current: PrismicRaw,
  position: number,
) => ({
  ...acc,
  ...current,
  appointment_reschedule_end_hours: listReducer(
    acc.appointment_reschedule_end_hours,
    current.appointment_reschedule_end_hours,
  ).map((time: StringMap) => time['hour_minute'] || time),
  appointment_reschedule_start_hours: listReducer(
    acc.appointment_reschedule_start_hours,
    current.appointment_reschedule_start_hours,
  ).map((time: StringMap) => time['hour_minute'] || time),
  contact_reasons: listReducer(acc.contact_reasons, current.contact_reasons),
  custom_vehicle_brands: listReducer(
    acc.custom_vehicle_brands,
    current.custom_vehicle_brands,
  ),
  custom_vehicle_types: listReducer(
    acc.custom_vehicle_types,
    current.custom_vehicle_types,
  ),
  custom_fuel_types: listReducer(
    acc.custom_fuel_types,
    current.custom_fuel_types,
  ),
  diagnosis_tree_context: listReducer(
    acc.diagnosis_tree_context,
    current.diagnosis_tree_context,
  ),
  pages_without_header: listReducer(
    acc.pages_without_header,
    current.pages_without_header,
  ),
  pages_without_footer: listReducer(
    acc.pages_without_footer,
    current.pages_without_footer,
  ),
  custom_steps: customStepReducer(
    acc.custom_steps,
    castCustomStepSlice(current.custom_steps),
  ),
  decision_tree: {
    ...acc.decision_tree,
    ...castListToObject(current, 'decision_tree', decisionTreeReducer),
  },
  decision_tree_text_to_hide: listReducer(
    acc.decision_tree_text_to_hide,
    current.decision_tree_text_to_hide,
  )
    .map((op: any) =>
      op['text_option'] ? op['text_option'].toLowerCase() : null,
    )
    .filter((op: any) => op !== null),
  emergency_banner_messages: {
    ...acc.emergency_banner_messages,
    ...castListToObject(current, 'emergency_banner_messages', emergencyBanner),
  },
  gtm_env: {
    ...acc.gtm_env,
    ...castListToObject(current, 'gtm_env', environmentObjectReducer),
  },
  identification: {
    ...acc.identification,
    ...castIdentificationToObject(current.identification),
  },
  map_default_location: {
    ...acc.map_default_location,
    ...castListToObject(current, 'map_default_location', defaultPrismicReducer),
  },
  map_valid_countries: listReducer(
    acc.map_valid_countries,
    current.map_valid_countries
      ?.filter((country: any) => !!country?.country_code)
      .map((country: any) => country.country_code),
  ),
  menu_links: listReducer(acc.menu_links, current.menu_links).map((data: any) =>
    clean(data, true),
  ),
  navigation_default: simpleListReducer(
    acc.navigation_default,
    castNavigationToList(current.navigation_default),
  ),
  navigation_desktop: simpleListReducer(
    acc.navigation_desktop,
    castNavigationToList(current.navigation_desktop),
  ),
  one_trust_env: {
    ...acc.one_trust_env,
    ...castListToObject(current, 'one_trust_env', environmentSingleKeyReducer),
  },
  po_tools_env: {
    ...acc.po_tools_env,
    ...castListToObject(current, 'po_tools_by_env', environmentObjectReducer),
  },
  google_optimize_experiments_list: {
    ...acc.google_optimize_experiments_list,
    ...castListToObject(
      current,
      'google_optimize_experiments_list',
      experimentsReducer,
    ),
  },
  regex: {
    ...acc.regex,
    ...castListToObject(current, 'regex', regexReducer),
  },
  policies: {
    ...acc.policies,
    ...castListToObject(current, 'policies', policyReducer),
  },
  service_qualifier_tree: {
    ...acc.service_qualifier_tree,
    ...castListToObject(
      current,
      'service_qualifier_tree',
      environmentSingleKeyReducer,
    ),
  },
  services_tree_context: listReducer(
    acc.services_tree_context,
    current.services_tree_context,
  ),
  survey_per_page: {
    ...acc.survey_per_page,
    ...castListToObject(current, 'survey_per_page', surveyReducer),
  },
  step_order: listReducer(
    acc.step_order,
    castStepOrderSliceToList(current.step_order),
  ),
  summary_config: listReducer(acc.summary_config, current.summary_config, [
    'id',
  ]),
  supported_languages: listReducer(
    acc.supported_languages,
    current.supported_languages
      ?.map((data: any) => Object.values(data)[0])
      .filter((value: any) => value !== undefined && value !== null),
  ),
  supported_phone_country_codes: listReducer(
    acc.supported_phone_country_codes,
    current.supported_phone_country_codes,
  ),
  events_with_provider_details: listReducer(
    acc.events_with_provider_details,
    current.events_with_provider_details
      ?.map((data: any) => Object.values(data)[0])
      .filter((value: any) => value !== undefined && value !== null),
  ),
  theme: getDefaultColors({
    ...acc.theme,
    ...getTheme(current),
    ...getBusinessTheme(current),
    ...getImages(current),
    ...getAdvancedCss(current),
    ...getColors(current, position),
  }),
  theme_colors: undefined,
  time_slot_options: listReducer(
    acc.time_slot_options,
    current.time_slot_options,
  ).map(timeSlotOptionsMap),
});

export const moveToKeys = (rawConfiguration: any): Configuration => {
  const {
    appointment_reschedule_end_hours,
    appointment_reschedule_start_hours,
    computable_contracts_static_questions,
    computable_contracts_default_dynamic_questions,
    contact_reasons,
    contact_us_motorway_safety_instructions,
    custom_fuel_types,
    custom_steps,
    custom_vehicle_brands,
    custom_vehicle_types,
    decisionTree,
    decision_tree,
    decision_tree_text_to_hide,
    diagnosis_tree_context,
    emergency_banner_messages,
    google_optimize_experiments_list,
    gtm_env,
    identification,
    map_default_location,
    map_valid_countries,
    menu_links,
    navigation_default,
    navigation_desktop,
    next_steps_case_created,
    next_steps_case_created_dropoff,
    next_steps_case_created_dropoff_success,
    next_steps_case_created_error,
    next_steps_case_created_excluded,
    next_steps_case_created_ongoing,
    next_steps_case_created_ongoing_icvc,
    one_trust_env,
    po_tools_env,
    po_tools_by_env,
    regex,
    policies,
    service_qualifier_tree,
    services_tree_context,
    survey_per_page,
    step_order,
    summary_config,
    supported_languages,
    supported_phone_country_codes,
    theme,
    theme_colors_business,
    time_slot_options,
    passenger_adult_options,
    passenger_child_options,
    pages_without_header,
    pages_without_footer,
    events_with_provider_details,
    ...rest
  } = rawConfiguration;
  const keys = Object.keys(rest)
    .sort()
    .reduce(
      (acc, current) => ({ ...acc, [current]: rest[current] }),
      {},
    ) as ConfigurationKeys;
  const environment = process.env.REACT_APP_environment as Environment;
  return {
    appointment_reschedule_end_hours,
    appointment_reschedule_start_hours,
    computable_contracts_static_questions,
    computable_contracts_default_dynamic_questions,
    contact_reasons,
    contact_us_motorway_safety_instructions,
    custom_vehicle_brands: castObjectToList(custom_vehicle_brands),
    custom_fuel_types: castObjectToList(custom_fuel_types),
    custom_steps,
    custom_vehicle_types: castObjectToList(custom_vehicle_types),
    decision_tree_text_to_hide,
    decision_tree,
    decisionTree,
    diagnosis_tree_context,
    emergency_banner_messages,
    events_with_provider_details,
    gtm_env,
    identification,
    keys,
    map_valid_countries,
    map_default_location,
    menu_links,
    navigation_default,
    navigation_desktop,
    next_steps_case_created,
    next_steps_case_created_dropoff,
    next_steps_case_created_dropoff_success,
    next_steps_case_created_error,
    next_steps_case_created_excluded,
    next_steps_case_created_ongoing,
    next_steps_case_created_ongoing_icvc,
    one_trust_env,
    google_optimize_experiments_list,
    pages_without_footer: castObjectToList(pages_without_footer),
    pages_without_header: castObjectToList(pages_without_header),
    passenger_adult_options,
    passenger_child_options,
    policies,
    po_tools: po_tools_env[environment],
    regex,
    service_qualifier_tree,
    services_tree_context,
    survey_per_page,
    step_order,
    summary_config,
    supported_languages,
    supported_phone_country_codes,
    theme,
    time_slot_options,
  };
};
